import { FC } from 'react';

import { usePage } from '@inertiajs/react';

import { csrfTokenHeader } from '../../services/CsrfTokenService';
import type {
    AdBanner as AdBannerData,
    Expansion,
    ProductType,
} from '../../types';
import { cn } from '../../utils';

type Props = {
    embedded?: boolean;
    className?: string;
};

type PageProps = {
    activeAdBanner?: AdBannerData;
    currentProductType?: ProductType;
    currentExpansion?: Expansion;
};

const normalizeBannerLink = (link?: string): string | null => {
    if (!link) {
        return null;
    }

    if (link.startsWith('/')) {
        return link;
    }

    try {
        const parsedUrl = new URL(link);
        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
            return parsedUrl.toString();
        }
    } catch {
        return null;
    }

    return null;
};

const isExternalLink = (link: string): boolean =>
    link.startsWith('http://') || link.startsWith('https://');

const trackBannerClick = (adBannerId: number) => {
    void fetch(`/ad_banners/${adBannerId}/click`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            ...csrfTokenHeader(),
        },
        credentials: 'same-origin',
        keepalive: true,
    });
};

export const AdBanner: FC<Props> = ({ embedded = false, className }) => {
    const { activeAdBanner, currentProductType, currentExpansion } =
        usePage<PageProps>().props;

    if (
        currentProductType ||
        currentExpansion ||
        !activeAdBanner ||
        !activeAdBanner.active
    ) {
        return null;
    }

    const imageSm =
        activeAdBanner.image_sm ||
        activeAdBanner.image_md ||
        activeAdBanner.image_large;
    const imageMd =
        activeAdBanner.image_md ||
        activeAdBanner.image_large ||
        activeAdBanner.image_sm;
    const imageLarge =
        activeAdBanner.image_large ||
        activeAdBanner.image_md ||
        activeAdBanner.image_sm;

    if (!imageSm) {
        return null;
    }

    const bannerLink = normalizeBannerLink(activeAdBanner.link);
    const opensInNewTab = bannerLink ? isExternalLink(bannerLink) : false;
    const bannerLinkRel = opensInNewTab
        ? 'sponsored nofollow noopener noreferrer'
        : 'sponsored nofollow';
    const bannerLinkTarget = opensInNewTab ? '_blank' : undefined;
    const bannerImage = (
        <picture>
            {imageLarge && (
                <source media="(min-width: 1024px)" srcSet={imageLarge} />
            )}
            {imageMd && <source media="(min-width: 768px)" srcSet={imageMd} />}
            <img
                src={imageSm}
                alt="Рекламний банер"
                className="h-auto w-full"
                loading="lazy"
                data-testid="ad-banner-image"
            />
        </picture>
    );

    return (
        <section
            className={cn(
                embedded
                    ? 'w-full'
                    : 'mx-auto mt-4 w-full max-w-[1600px] px-4 sm:px-6 md:mt-0 lg:px-8',
                className,
            )}
            data-testid="ad-banner"
        >
            {bannerLink ? (
                <a
                    href={bannerLink}
                    className="block cursor-pointer overflow-hidden rounded-xl border border-border/60 shadow-sm transition-opacity hover:opacity-95"
                    rel={bannerLinkRel}
                    target={bannerLinkTarget}
                    onClick={() => trackBannerClick(activeAdBanner.id)}
                    data-testid="ad-banner-link"
                >
                    {bannerImage}
                </a>
            ) : (
                <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
                    {bannerImage}
                </div>
            )}
        </section>
    );
};
